/**
 * Article-end slot coordination
 * @module lib/insights/articleEndSlot
 *
 * The bottom of the article is a single exclusive slot: when a survey,
 * prediction, or contest embed renders there, no other article-end unit
 * (newsletter box or member ad) may load alongside it. The
 * embed injection settles the slot (occupied or free) once its decision is
 * known; the other units wait on that decision before injecting.
 */

const DEFAULT_DECISION_TIMEOUT_MS = 5000;

let slotState = null;

function ensureState() {
  if (!slotState) {
    let resolve;
    const promise = new Promise((res) => {
      resolve = res;
    });
    slotState = { promise, resolve, settled: false, extensionMs: 0 };
  }
  return slotState;
}

/**
 * Record the embed decision. occupied=true means an embed item was inserted
 * at article end; false means the slot is free. First settle wins.
 */
function settleArticleEndSlot(occupied) {
  const state = ensureState();
  if (state.settled) return;
  state.settled = true;
  state.resolve(occupied === true);
}

/**
 * Give the embed injection `ms` more before waiters fall back to free, for a
 * bounded wait it starts before its fetch (a brand lift study reader's
 * group, lib/insights/study.js). Applies to current and later waiters.
 */
function extendArticleEndSlotDecision(ms) {
  const state = ensureState();
  if (state.settled) return;
  state.extensionMs += Math.max(0, Number(ms) || 0);
}

/**
 * Resolves true when an embed item owns the article-end slot, false when the
 * slot is free. Falls back to free after timeoutMs (plus any extension) so a
 * hung embed fetch never suppresses the other units for the whole pageview.
 */
function waitForArticleEndSlot(timeoutMs = DEFAULT_DECISION_TIMEOUT_MS) {
  const state = ensureState();
  if (state.settled) return state.promise;
  let timer = null;
  let extendedMs = 0;
  const timeout = new Promise((resolve) => {
    const expire = () => {
      const pendingMs = state.extensionMs - extendedMs;
      if (pendingMs > 0) {
        extendedMs = state.extensionMs;
        timer = setTimeout(expire, pendingMs);
        return;
      }
      resolve(false);
    };
    timer = setTimeout(expire, timeoutMs);
  });
  return Promise.race([state.promise, timeout]).then((occupied) => {
    if (timer) clearTimeout(timer);
    return occupied === true;
  });
}

// Test-only.
function resetArticleEndSlot() {
  slotState = null;
}

module.exports = {
  DEFAULT_DECISION_TIMEOUT_MS,
  settleArticleEndSlot,
  waitForArticleEndSlot,
  extendArticleEndSlotDecision,
  resetArticleEndSlot
};
